import { supabase } from '../lib/supabase';

/**
 * Escalation Service
 * Handles intelligent escalation workflows for travel requests
 */

class EscalationService {
  /**
   * Get all active escalations for current user
   */
  async getMyEscalations() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('travel_escalations')?.select(`
          *,
          travel_request:travel_requests(
            request_number,
            traveler:user_profiles!travel_requests_traveler_id_fkey(full_name, email),
            destination,
            travel_type,
            start_date,
            end_date,
            estimated_budget,
            status
          ),
          escalation_rule:escalation_rules(rule_name, description),
          assigned_user:user_profiles!travel_escalations_assigned_to_fkey(full_name, email)
        `)?.or(`assigned_to.eq.${user?.id},escalated_from.eq.${user?.id}`)?.order('escalated_at', { ascending: false });

      if (error) throw error;

      return this._convertToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching escalations:', error);
      throw error;
    }
  }

  /**
   * Get escalations for a specific travel request
   */
  async getEscalationsForRequest(requestId) {
    try {
      const { data, error } = await supabase?.from('travel_escalations')?.select(`
          *,
          escalation_rule:escalation_rules(rule_name, description, target_approver_level),
          assigned_user:user_profiles!travel_escalations_assigned_to_fkey(full_name, email),
          escalated_from_user:user_profiles!travel_escalations_escalated_from_fkey(full_name, email)
        `)?.eq('travel_request_id', requestId)?.order('escalated_at', { ascending: false });

      if (error) throw error;

      return this._convertToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching request escalations:', error);
      throw error;
    }
  }

  /**
   * Get escalation statistics
   */
  async getEscalationStats() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get counts by status
      const { data: statusCounts, error: statusError } = await supabase?.from('travel_escalations')?.select('status')?.eq('assigned_to', user?.id);

      if (statusError) throw statusError;

      const stats = {
        totalEscalations: statusCounts?.length || 0,
        pending: statusCounts?.filter(e => e?.status === 'pending')?.length || 0,
        inProgress: statusCounts?.filter(e => e?.status === 'in_progress')?.length || 0,
        escalated: statusCounts?.filter(e => e?.status === 'escalated')?.length || 0,
        resolved: statusCounts?.filter(e => e?.status === 'resolved')?.length || 0
      };

      // Get overdue count (pending for more than 24 hours)
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString();
      const { data: overdueData, error: overdueError } = await supabase?.from('travel_escalations')?.select('id')?.eq('assigned_to', user?.id)?.in('status', ['pending', 'in_progress'])?.lt('escalated_at', twentyFourHoursAgo);

      if (overdueError) throw overdueError;

      stats.overdue = overdueData?.length || 0;

      return stats;
    } catch (error) {
      console.error('Error fetching escalation stats:', error);
      throw error;
    }
  }

  /**
   * Manually trigger escalation check
   */
  async triggerEscalationCheck() {
    try {
      const { error } = await supabase?.rpc('check_and_create_escalations');
      
      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error triggering escalation check:', error);
      throw error;
    }
  }

  /**
   * Escalate to next level
   */
  async escalateToNextLevel(escalationId) {
    try {
      const { data, error } = await supabase?.rpc('escalate_to_next_level', {
        escalation_uuid: escalationId
      });

      if (error) throw error;

      return { success: data };
    } catch (error) {
      console.error('Error escalating to next level:', error);
      throw error;
    }
  }

  /**
   * Resolve an escalation
   */
  async resolveEscalation(escalationId, resolutionNotes) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('travel_escalations')?.update({
          status: 'resolved',
          resolved_at: new Date()?.toISOString(),
          resolved_by: user?.id,
          resolution_notes: resolutionNotes,
          updated_at: new Date()?.toISOString()
        })?.eq('id', escalationId)?.select()?.single();

      if (error) throw error;

      // Create history record
      await supabase?.from('escalation_history')?.insert({
        escalation_id: escalationId,
        action: 'resolved',
        to_status: 'resolved',
        performed_by: user?.id,
        notes: resolutionNotes
      });

      return this._convertToCamelCase(data);
    } catch (error) {
      console.error('Error resolving escalation:', error);
      throw error;
    }
  }

  /**
   * Update escalation status
   */
  async updateEscalationStatus(escalationId, status, notes = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData = {
        status,
        updated_at: new Date()?.toISOString()
      };

      if (notes) {
        updateData.escalation_notes = notes;
      }

      const { data, error } = await supabase?.from('travel_escalations')?.update(updateData)?.eq('id', escalationId)?.select()?.single();

      if (error) throw error;

      // Create history record
      await supabase?.from('escalation_history')?.insert({
        escalation_id: escalationId,
        action: 'status_updated',
        to_status: status,
        performed_by: user?.id,
        notes: notes
      });

      return this._convertToCamelCase(data);
    } catch (error) {
      console.error('Error updating escalation status:', error);
      throw error;
    }
  }

  /**
   * Get escalation history
   */
  async getEscalationHistory(escalationId) {
    try {
      const { data, error } = await supabase?.from('escalation_history')?.select(`
          *,
          performer:user_profiles!escalation_history_performed_by_fkey(full_name, email)
        `)?.eq('escalation_id', escalationId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return this._convertToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching escalation history:', error);
      throw error;
    }
  }

  /**
   * Get active escalation rules
   */
  async getActiveRules() {
    try {
      const { data, error } = await supabase?.from('escalation_rules')?.select('*')?.eq('is_active', true)?.order('threshold_hours', { ascending: true });

      if (error) throw error;

      return this._convertToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching escalation rules:', error);
      throw error;
    }
  }

  /**
   * Subscribe to escalation changes
   */
  subscribeToEscalations(userId, callback) {
    const subscription = supabase?.channel('travel_escalations_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'travel_escalations',
          filter: `assigned_to=eq.${userId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return subscription;
  }

  /**
   * Convert snake_case to camelCase
   */
  _convertToCamelCase(data) {
    if (Array.isArray(data)) {
      return data?.map(item => this._convertObjectToCamelCase(item));
    }
    return this._convertObjectToCamelCase(data);
  }

  _convertObjectToCamelCase(obj) {
    if (!obj || typeof obj !== 'object') return obj;

    const converted = {};
    for (const key in obj) {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      const value = obj?.[key];
      
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        converted[camelKey] = this._convertObjectToCamelCase(value);
      } else if (Array.isArray(value)) {
        converted[camelKey] = value?.map(item => 
          typeof item === 'object' ? this._convertObjectToCamelCase(item) : item
        );
      } else {
        converted[camelKey] = value;
      }
    }
    return converted;
  }
}

export default new EscalationService();